import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  styled,
  Box,
  Typography,
  Grid,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import * as ApprovalActions from '../../../store/actions/approval.action';
import { getApprovalState } from '../../../store/selector/approval.selector';
import Services from '../../../shared/services';
import ConfirmationModal from '../../../shared/components/ui/modal/confirmation-modal';
import DynamicCrumbs from '../../../shared/components/ui/dashboard-layout/dynamic-crumbs';
import DynamicTabLink from '../../../shared/components/ui/dynamic-tabs/dynamic-tab-link';
import OptionsMenu from '../../../shared/components/ui/menu/options-menu';
import { IApprovalApplication, IDecide } from '../../../shared/interfaces/approval.interface';
import { IOptionsButton } from '../../../shared/interfaces/options.interface';
import { approvalActions } from '../../../shared/constants/approval-actions';
import { APPROVALS_TABS_CONSTANT } from '../../../shared/constants/approvals-tabs.constant';

const styles = {
  container: {
    background: '#ffffff',
    p: 4,
  },

  header: {
    display: 'flex',
    flexDirection: { sm: 'row', xs: 'column' },
    justifyContent: 'space-between',
    alignItems: { sm: 'center', xs: 'flex-start' },
    mb: 3,
  },

  actionsButton: {
    padding: '0.5rem 1.5rem',
    borderRadius: '0.5rem',
    width: { sm: 'auto', xs: '100%' },
    mt: { sm: 0, xs: '2rem' },
  },
};

const Data = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.dark,
  fontSize: '1rem',
}));

const ApprovalsInformation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { query, selected } = useSelector(getApprovalState);
  const { slug } = useParams<string>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [data, setData] = useState<IApprovalApplication>();
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState<string>('');
  const [applicationStatus, setApplicationStatus] = useState<string>('');
  const [params, setParams] = useState<IDecide>({
    mode: '',
    remarks: '',
  });
  const [options, setOptions] = useState<Array<IOptionsButton> | undefined>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (slug) {
      const q = { ...query, q: slug };

      if (query) dispatch(ApprovalActions.ApprovalFetchApplication(q));
    }
  }, []);

  useEffect(() => {
    if (selected) {
      setData(selected.application);
    }
  }, [selected]);

  const renderActions = () => {
    if (data) {
      const { status } = selected;
      const { step } = data;

      if (status === 'created' && step === '1') {
        setOptions(approvalActions('withOngoing', onOpenModal, 'Approved'));
        setApplicationStatus('Approved');
      } else if (step === '3') {
        setOptions(approvalActions('withoutOngoing', onOpenModal, 'Completed'));
        setApplicationStatus('Completed');
      } else if (step === '4') {
        setOptions(approvalActions('withoutOngoing', onOpenModal, 'Finalized'));
        setApplicationStatus('Finalized');
      }
    } else {
      setOptions(approvalActions('withoutOngoing', onOpenModal, 'Approved'));
      setApplicationStatus('Approved');
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    renderActions();
    setAnchorEl(event.currentTarget);
  };

  const onOpenModal = (action: string) => {
    setMode(action);
    setModalOpen(true);
  };

  const onModalAction = (e: string, remarks?: string) => {
    if (e === 'confirm' && remarks) {
      setParams((prev: IDecide) => {
        return {
          ...prev,
          mode: mode,
          remarks: remarks,
        };
      });
    } else {
      setModalOpen(false);
    }
  };

  useEffect(() => {
    if (params.mode) {
      onApproved();
    }
  }, [params]);

  useEffect(() => {
    renderActions();
  }, []);

  const onApproved = async () => {
    if (slug) {
      const { errors, success } = await Services.api.approval.decide(slug, params);

      if (success) {
        Services.toast('success', 'Application approved successfully');
        navigate('/approvals');
        setModalOpen(false);
      } else {
        const [{ message }] = errors;

        setModalOpen(false);
        Services.toast('error', message);
      }
    }
  };

  return (
    <>
      <Box sx={styles.header}>
        <Box>
          <DynamicCrumbs />

          {data && (
            <Typography sx={{ fontSize: '1.5rem' }}>
              {data.lastname}, {data.firstname}, {data.middlename} - {data?.code}
            </Typography>
          )}
        </Box>

        {data && (
          <Box>
            <Button
              variant='contained'
              sx={styles.actionsButton}
              disableElevation
              onClick={handleClick}
              endIcon={<KeyboardArrowDownIcon />}
            >
              Actions
            </Button>

            <OptionsMenu
              open={open}
              handleClose={handleClose}
              anchorEl={anchorEl}
              optionItems={options}
            />
          </Box>
        )}
      </Box>

      <DynamicTabLink tabs={APPROVALS_TABS_CONSTANT} />

      <Box sx={{ my: 2 }}>
        {data && (
          <Grid container sx={styles.container}>
            <Grid
              item
              xs={12}
              sx={{ display: 'flex', flexDirection: 'column', mb: '2rem' }}
              gap={'0.2rem'}
            >
              <Typography color='primary' sx={{ fontSize: '1.5rem', mb: { sm: 0, xs: '1rem' } }}>
                Personal Information
              </Typography>

              {!isMobile && <Divider sx={{ mb: '1rem', width: '25rem' }} />}

              <Data>
                Name: {data.lastname}, {data.firstname}, {data.middlename}
              </Data>

              <Data>Birthdate: {data.birthdate} </Data>

              <Data>Email Address: {data.email_address}</Data>

              <Data>Mobile Number: {data.mobile_no} </Data>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{ display: 'flex', flexDirection: 'column', mb: '2rem' }}
              gap={'0.2rem'}
            >
              <Typography color='primary' sx={{ fontSize: '1.5rem', mb: { sm: 0, xs: '1rem' } }}>
                Employment Information
              </Typography>

              {!isMobile && <Divider sx={{ mb: '1rem', width: '25rem' }} />}

              <Data>Employee Code: {data.code}</Data>

              <Data>Affiliate Description: {data.company}</Data>

              <Data>Department: {data.department}</Data>

              <Data>Position: {data.position}</Data>

              <Data>Base of Assignment: {data.base_of_assignment}</Data>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{ display: 'flex', flexDirection: 'column', mb: '1rem' }}
              gap={'0.2rem'}
            >
              <Typography color='primary' sx={{ fontSize: '1.5rem', mb: { sm: 0, xs: '1rem' } }}>
                Clearance Information
              </Typography>

              {!isMobile && <Divider sx={{ mb: '1rem', width: '25rem' }} />}

              <Data>Date Hired: {data.hiring_date}</Data>

              <Data>Date of Clearance Application: {data.clearance_application_date}</Data>

              <Data>Last Working Day: {data.last_working_day}</Data>

              <Data>Effective Date of Separation: {data.effective_separation_date}</Data>

              {data.completion_date && <Data>Completion Date: {data.completion_date}</Data>}

              <Data sx={{ width: { md: '50%', xs: '100%' } }}>
                Reason for Separation: {data.reason}
              </Data>

              {data.reason === 'Dissatisfaction at Work' && (
                <Data sx={{ width: { md: '50%', xs: '100%' } }}>
                  Dissatisfaction Cause: {data.dissatisfaction_cause}
                </Data>
              )}

              {data.reason === 'End of Contract' && (
                <Data sx={{ width: { md: '50%', xs: '100%' } }}>
                  End of Contract Cause: {data.end_contract_cause}
                </Data>
              )}
            </Grid>
          </Grid>
        )}
      </Box>

      <ConfirmationModal
        isModalOpen={modalOpen}
        title={'Confirmation'}
        message={`The status of this application will be set to "${
          mode === 'ongoing' ? 'Ongoing' : mode === 'rejected' ? 'Rejected' : applicationStatus
        }". Are you sure you want to proceed?`}
        isRemarks={true}
        onClickModal={(e: string, remarks?: string) => onModalAction(e, remarks)}
      />
    </>
  );
};

export default ApprovalsInformation;
